import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  IProviderResponseDTO,
  IServicingProviderDTO,
  IServicingProviderPresenting,
  IServicingProviderResponseDTO,
} from '@core/model';
import { ApplicationStateService, AuthRequestInitialService } from '@core/service';
import { API_URL } from '@core/constants';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ServicingProviderService {
  mappedListOfServicingProviders$: Observable<IServicingProviderPresenting[]> =
    this.authRequestInitialService.listOfServicingProviders$.pipe(
      map((providers) =>
        providers.map((provider: IServicingProviderDTO) => {
          const {
            providerAccountId,
            providerName,
            physicalStreet,
            physicalCity,
            physicalState,
            physicalZip,
            locationPhone,
            locationFax,
            locationFaxConfirmed,
            outOfMembersNetwork,
          } = provider.providerAccountDTO;
          const address = [
            physicalStreet,
            physicalCity,
            physicalState,
            physicalZip,
          ]
            .filter((item) => !!item)
            .join(', ');

          return {
            id: providerAccountId,
            name: providerName,
            address,
            phone: locationPhone || '',
            fax: locationFax || '',
            isFaxConfirmed: locationFaxConfirmed ? 'Yes' : 'No',
            isInNetworkForMember: outOfMembersNetwork ? 'No' : 'Yes',
          };
        })
      )
    );

  textForFiltering$ = new BehaviorSubject<string>('');

  constructor(
    private authRequestInitialService: AuthRequestInitialService,
    private applicationStateService: ApplicationStateService,
    public http: HttpClient,
    @Inject(API_URL) private apiUrl: string
  ) {
  }

  private get baseUrl(): string {
    return `${this.apiUrl}/api/tenants/${this.applicationStateService.tenantId}`;
  }

  searchServicingProviders(npi: string, memberId: string = null): Observable<IServicingProviderDTO[]> {
    const url = `${this.baseUrl}/providers/servicing/${npi}?memberId=${memberId ?? this.applicationStateService.memberId}`
    return this.http.get<IServicingProviderDTO[]>(url);
  }

  createUpdateServicingProvider(
    authorizationByProductId: string,
    providerAccountId: string,
    providerPgpId: string
  ): Observable<IProviderResponseDTO> {
    return this.http.put<IProviderResponseDTO>(
      `${this.baseUrl}/authorizations/${authorizationByProductId}/providers/servicing`,
      { providerAccountId, providerPgpId }
    );
  }

  fetchAllProvidersForUser(): Observable<IServicingProviderDTO[]> {
    return this.http.get<IServicingProviderDTO[]>(
      `${this.baseUrl}/providers/servicing/user?memberId=${this.applicationStateService.memberId}`
    );
  }

  findProviderById(id: string): IServicingProviderDTO {
    return this.authRequestInitialService.listOfServicingProviders$.value
      .find((provider: IServicingProviderDTO) => provider.providerAccountDTO.providerAccountId === id);
  }

  setTextForFiltering(val: string): void {
    this.textForFiltering$.next(val);
  }

  getProviderForAuthorization(authorizationByProductId: string): Observable<IServicingProviderDTO> {
    return this.http.get<IServicingProviderResponseDTO>(`${this.baseUrl}/authorizations/${authorizationByProductId}/providers/servicing`)
      .pipe(map((data) => {
        return data ? {
          providerAccountDTO: data.providerAccountDTO,
          providerPgpDTOList: [data.providerPgpDTO]
        } : null;
      }));
  }
}
